import { toInteger } from 'lodash-es';

import { ANNOTATIONS_PADDING_BOTTOM } from './constants.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { AnnotationsNumericalDisplay } from '../../../core/components/annotations/components/displays/AnnotationsNumericalDisplay.js';
import { AnnotationsScrollWrapper } from '../../../core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import {
  DEFAULT_INTEGRATION_TEXT_OVERFLOW,
  DEFAULT_SHOW_LABELS,
  SCROLLBAR_THRESHOLD,
} from '../../../core/components/annotations/constants.js';
import type { AnnotationsActionsPayload } from '../../../core/components/annotations/types/annotations.js';
import type { SelectionState } from '../../../core/components/annotations/types/state.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { AREAS } from '../../constants/histogram-grid-area-rules.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramAnnotationsConfig } from '../../hooks/useHistogramAnnotationsConfig.js';
import { useHistogramXScale } from '../../hooks/useHistogramXScale.js';
import { useXAxisConfig } from '../../hooks/useXAxisConfig.js';

const COMPONENT_NAME = 'AnnotationsChart';

export const HistogramAnnotationsArea = () => {
  const { xScale } = useHistogramXScale();
  const config = useHistogramAnnotationsConfig();
  const {
    tracks,
    showLabels = DEFAULT_SHOW_LABELS,
    annotationsActions,
    tooltip,
  } = config;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(config);
  const setState = useSetState();
  const { width: trackWidth } = useGridPlotSize();

  const handleUpdateIndicators = (event: Partial<SelectionState>) => {
    setState((prev) => ({
      ...prev,
      annotations: {
        ...prev.annotations,
        ...event,
      },
    }));
  };

  const { formatter = (value) => `${toInteger(value)}` } = useXAxisConfig();

  return (
    <ChartGrid.GridArea
      paddingBottom={ANNOTATIONS_PADDING_BOTTOM}
      gridArea={AREAS.annotations}
    >
      {({ availableWidth, availableHeight }) => {
        const labelOffset = availableWidth - trackWidth;
        return (
          <AnnotationsScrollWrapper
            height={availableHeight}
            width={availableWidth + SCROLLBAR_THRESHOLD}
          >
            <AnnotationsNumericalDisplay
              tracks={tracks}
              width={availableWidth}
              scale={xScale}
              showLabels={showLabels}
              labelsOffset={labelOffset}
              annotationsActions={(
                annotation: AnnotationsActionsPayload | undefined,
              ) => annotation && annotationsActions?.(annotation)}
              markerGroupHover={handleUpdateIndicators}
              formatter={formatter}
              textOverflow={DEFAULT_INTEGRATION_TEXT_OVERFLOW}
              tooltipConfig={tooltip}
              {...behaviorTrackingProps}
              data-dt-component={COMPONENT_NAME}
            />
          </AnnotationsScrollWrapper>
        );
      }}
    </ChartGrid.GridArea>
  );
};

HistogramAnnotationsArea['displayName'] = 'HistogramAnnotationsArea';
